import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { SettingService, SiteSetting, Lang } from '../../../services/setting.service';

@Component({
  selector: 'app-map',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './map.html',
  styleUrl: './map.css',
})
export class Map implements OnInit, OnDestroy {
  private settingSvc = inject(SettingService);
  private translate  = inject(TranslateService);
  private sanitizer  = inject(DomSanitizer);

  setting: SiteSetting | null = null;
  safeMapUrl: SafeResourceUrl | null = null;
  currentLang: Lang = 'fr';

  private langSub?: Subscription;

  ngOnInit(): void {
    this.currentLang = (this.translate.currentLang as Lang) || 'fr';
    this.load(this.currentLang);

    this.langSub = this.translate.onLangChange.subscribe(e => {
      this.currentLang = e.lang as Lang;
      this.load(this.currentLang);
    });
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  private load(lang: Lang): void {
    this.settingSvc.getSettings(lang).subscribe({
      next: s => {
        this.setting = s;
        this.refreshMapUrl();
      },
      error: err => console.error('Map section load error', err),
    });
  }

  /**
   * Construit l'URL sécurisée de l'iframe. Filtre HTTPS et domaine Google Maps
   * avant de bypasser la sécurité Angular pour le rendu.
   */
  private refreshMapUrl(): void {
    const raw = (this.setting?.google_map?.embed_url ?? '').trim();
    if (!raw || !this.isSafeGoogleMapsUrl(raw)) {
      this.safeMapUrl = null;
      return;
    }
    this.safeMapUrl = this.sanitizer.bypassSecurityTrustResourceUrl(raw);
  }

  /** Whitelist : HTTPS sur un domaine google.com/maps officiel uniquement. */
  private isSafeGoogleMapsUrl(url: string): boolean {
    return /^https:\/\/(www\.)?google\.(com|fr|mr)\/maps(\/d)?(\/embed)?[/?]/i.test(url);
  }

  /** Section affichée uniquement si activée + URL valide. */
  get shouldShow(): boolean {
    return !!this.setting?.google_map?.enabled && !!this.safeMapUrl;
  }

  /** Titre localisé avec fallback. */
  get title(): string {
    const m = this.setting?.google_map;
    if (!m) return '';
    return m.title_text || m.title?.[this.currentLang] || m.title?.fr || '';
  }

  /** Description localisée avec fallback. */
  get description(): string {
    const m = this.setting?.google_map;
    if (!m) return '';
    return m.description_text || m.description?.[this.currentLang] || m.description?.fr || '';
  }
}
